/** Wire protocol: message names, payload shapes, and the enums used in state. */

import type { ItemKind } from './worldgen.js';

// ---------------------------------------------------------------------------
// Enums (encoded as uint8 in schema state)
// ---------------------------------------------------------------------------

export const MatchPhase = {
  Lobby: 0,
  Playing: 1,
  /** Enough towers activated; the helicopter is on its way. */
  RescueInbound: 2,
  /** Chopper has landed; hold the zone. */
  Extracting: 3,
  Won: 4,
  Lost: 5,
} as const;
export type MatchPhase = (typeof MatchPhase)[keyof typeof MatchPhase];

export const LifeState = {
  Alive: 0,
  /** In the grab/thrash sequence — still rendered, no longer in control. */
  Caught: 1,
  Dead: 2,
  /** Watching teammates after death. */
  Spectating: 3,
} as const;
export type LifeState = (typeof LifeState)[keyof typeof LifeState];

export const Stance = {
  Standing: 0,
  Crouching: 1,
  Sprinting: 2,
  Climbing: 3,
  Hiding: 4,
} as const;
export type Stance = (typeof Stance)[keyof typeof Stance];

export const MonsterState = {
  Dormant: 0,
  Roam: 1,
  Investigate: 2,
  Hunt: 3,
  Chase: 4,
  Kill: 5,
} as const;
export type MonsterState = (typeof MonsterState)[keyof typeof MonsterState];

/** Positional audio cues the server broadcasts so everyone hears the same world. */
export const CueKind = {
  DistantStomp: 0,
  NearStomp: 1,
  Siren: 2,
  DistantSiren: 3,
  TreeCrash: 4,
  Roar: 5,
  Screech: 6,
  /**
   * Forest ambience. Deliberately distinct from anything the creature makes.
   *
   * These are the only cues allowed to come from somewhere he is not. Every
   * stomp and every siren originates at his true position, because working out
   * where he is by ear is the core skill of the game — and cues that lie about
   * his direction destroy it.
   */
  BranchSnap: 7,
  Crow: 8,
} as const;
export type CueKind = (typeof CueKind)[keyof typeof CueKind];

// ---------------------------------------------------------------------------
// Client -> Server
// ---------------------------------------------------------------------------

export const ClientMsg = {
  /** High-frequency movement report. Sanity-checked, not simulated, by the server. */
  Move: 'm',
  SetName: 'name',
  ToggleFlashlight: 'fl',
  ToggleRadio: 'rad',
  Interact: 'int',
  DropItem: 'drop',
  UseItem: 'use',
  SelectItem: 'sel',
  /** Tower transmit minigame input. */
  TransmitTune: 'tune',
  StartTransmit: 'tx+',
  StopTransmit: 'tx-',
  EnterHiding: 'hide+',
  ExitHiding: 'hide-',
  HoldBreath: 'breath',
  StartMatch: 'start',
  Ready: 'ready',
  /**
   * WebRTC signalling for proximity voice, relayed peer-to-peer through the
   * room. The server never sees audio — only the SDP/ICE handshake, which it
   * forwards blindly to the addressed session.
   */
  VoiceSignal: 'vsig',
  /**
   * The client's own microphone level, throttled to VOICE.SEND_HZ.
   *
   * This is the one part of voice the server *does* care about: it feeds the
   * noise system, so talking is audible to the monster like any other sound.
   */
  VoiceLevel: 'vlvl',
} as const;

export interface MoveMsg {
  x: number;
  y: number;
  z: number;
  yaw: number;
  pitch: number;
  stance: Stance;
  /**
   * Stamina, owned by the client.
   *
   * Clients already own their movement, and stamina is purely a movement
   * constraint. Simulating it independently on both sides meant the bar the
   * player watched and the value that gated sprinting were different numbers
   * that drifted apart — so the HUD could show plenty of stamina while sprint
   * refused to engage. The server clamps this rather than recomputing it.
   */
  stamina: number;
  /** Client's own tick counter, echoed back for latency estimation. */
  t: number;
}

export interface InteractMsg {
  /** What the client believes it is interacting with. Server re-validates. */
  kind: 'item' | 'tower' | 'hiding' | 'revive';
  id: number;
  /**
   * Revive only: whose body to bring round.
   *
   * A session id rather than an index into the player map. The index shifts
   * the moment anybody disconnects, so a reviver who pressed E on one body
   * could have the server wake a different person entirely.
   */
  sessionId?: string;
}

export interface TuneMsg {
  /** Normalised dial position, 0..1. */
  dial: number;
}

export interface UseItemMsg {
  kind: ItemKind;
}

export interface VoiceSignalMsg {
  /** Target session. The server validates it and relays, nothing more. */
  to: string;
  /** JSON-encoded SDP offer/answer or ICE candidate. Opaque to the server. */
  data: string;
}

export interface VoiceLevelMsg {
  /** Measured mic level, 0..1. Server clamps; voiceNoise() maps it to noise. */
  level: number;
}

// ---------------------------------------------------------------------------
// Server -> Client
// ---------------------------------------------------------------------------

export const ServerMsg = {
  /** Sent once on join: the seed everything else is derived from. */
  Welcome: 'welcome',
  /** Positional audio + screen-shake cue. */
  Cue: 'cue',
  /** A player was grabbed. Triggers the kill cinematic on all clients. */
  Caught: 'caught',
  /** Freeze this player and turn their head to look at the monster. */
  Confront: 'confront',
  /** A tower finished transmitting; carries the unlocked clue. */
  ClueUnlocked: 'clue',
  /** Rejected movement — client must snap back. */
  Correction: 'corr',
  MatchEnded: 'end',
  Notice: 'notice',
  /** Relayed voice signalling from another session. */
  VoiceSignal: 'vsig',
} as const;

/**
 * Structurally RTCIceServer, but declared here so shared never depends on DOM
 * types — the server imports this file too.
 */
export interface IceServerConfig {
  urls: string;
  username?: string;
  credential?: string;
}

export interface VoiceRelayMsg {
  /** Originating session. Stamped by the server, never trusted from clients. */
  from: string;
  data: string;
}

export interface WelcomeMsg {
  seed: number;
  /**
   * Sent alongside the seed because it feeds world generation — the client
   * cannot build a matching forest without it.
   */
  difficulty: number;
  code: string;
  sessionId: string;
  /** Server tick when this client joined, for clock alignment. */
  serverTime: number;
  /**
   * ICE servers for the voice mesh, so TURN credentials live in one place —
   * the server's environment — instead of being baked into client builds.
   */
  ice: IceServerConfig[];
}

export interface CueMsg {
  kind: CueKind;
  x: number;
  y: number;
  z: number;
  /** 0..1 intensity, already accounting for distance-independent factors. */
  gain: number;
}

export interface ConfrontMsg {
  /** Who loses control. Only ever the monster's current target. */
  sessionId: string;
  /** Where to look — his position, not his feet. */
  x: number;
  y: number;
  z: number;
  duration: number;
}

export interface CaughtMsg {
  sessionId: string;
  /** Monster position at the moment of the grab, for camera framing. */
  mx: number;
  my: number;
  mz: number;
  /** Deterministic seed so every client renders the same thrash. */
  seed: number;
}

export interface ClueMsg {
  towerId: number;
  clueIndex: number;
  text: string;
  towersActivated: number;
  towersRequired: number;
}

export interface CorrectionMsg {
  x: number;
  y: number;
  z: number;
  reason: string;
}

export interface MatchEndedMsg {
  won: boolean;
  reason: string;
  survivors: string[];
}

export interface NoticeMsg {
  text: string;
  /** Seconds to display. */
  duration: number;
}
